import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import { UPLOAD_INPUT_TYPES } from '__components/Kizen/FileInputWithImage';
import {
  StyledDescriptionInputWrapper,
  StyledFilePicker,
  StyledTestExplanation,
} from 'pages/KnowledgeSettings/styles';
import TextInput from '__components/Inputs/TextInput';
import { KnowledgeSource } from '@kizen/api/knowledge';
import { useUploadFile } from '__hooks/uploadFiles/useUploadFile';
import { useFiles } from '__hooks/uploadFiles/useFiles';
import { KnowledgeTypeSelect } from './KnowledgeTypeSelect';

const ALLOWED_EXTENSIONS = `text/*, application/pdf`;

export type OnConfirmAddFileParams = {
  description: string;
  s3Object: KnowledgeSource['s3_object'];
  knowledgeType: KnowledgeSource['knowledge_type'];
};

type AddKnowledgeSourceModalProps = {
  onConfirm: ({
    description,
    s3Object,
  }: OnConfirmAddFileParams) => Promise<void>;
  onHide: () => void;
  knowledgeType: KnowledgeSource['knowledge_type'];
  showKnowledgeTypeSelect?: boolean;
};

export const AddKnowledgeSourceModal: FC<AddKnowledgeSourceModalProps> = ({
  onConfirm,
  onHide,
  knowledgeType: knowledgeTypeProp,
  showKnowledgeTypeSelect = false,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState<string>('');
  const [knowledgeType, setKnowledgeType] =
    useState<KnowledgeSource['knowledge_type']>(knowledgeTypeProp);

  const uploadFile = useUploadFile(false, null, 'knowledge_source');

  const [, { addFiles }] = useFiles({
    uploadFile,
  });

  const onChange = (files: File[]) => {
    if (files.length) {
      setFile(files[0]);
    }
    return null;
  };

  const onRemove = () => {
    setFile(null);
  };

  const onDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const handleAdd = useCallback(async () => {
    if (file instanceof File) {
      const createdFiles = await addFiles([file]);
      if (createdFiles.length) {
        const uploadedFile = createdFiles[0];

        await onConfirm({
          description: description || file.name,
          s3Object: uploadedFile,
          knowledgeType,
        });
      }
    }
  }, [file, onConfirm, addFiles, description, knowledgeType]);

  return (
    <ConfirmationModal
      heading={t('Upload Knowledge Source')}
      buttonText={t('Add')}
      disabled={!file}
      show
      onConfirm={handleAdd}
      onHide={onHide}
    >
      <StyledTestExplanation>
        {t(
          'Uploaded files will be provided to the Kizen knowledge agent to answer questions to your users. All users will have access to this information, and we use LLMs to generate responses, so ensure that no sensitive PII data is in the uploaded files.'
        )}
      </StyledTestExplanation>
      {showKnowledgeTypeSelect ? (
        <StyledDescriptionInputWrapper>
          <KnowledgeTypeSelect
            value={knowledgeType}
            onChange={setKnowledgeType}
            margin
          />
        </StyledDescriptionInputWrapper>
      ) : null}
      <StyledFilePicker
        label={t('Upload file')}
        labelInfo={t('Supported filetypes: Text, CSV, PDF')}
        labelInfoPlacement="top"
        onChange={onChange}
        onRemove={onRemove}
        file={file}
        showImage={false}
        type={UPLOAD_INPUT_TYPES.AVATAR}
        acceptedExtensions={ALLOWED_EXTENSIONS}
        id="upload-knowledge-source"
      />
      <StyledDescriptionInputWrapper>
        <TextInput
          label={t('Description')}
          placeholder={t('Defaults to Filename')}
          value={description}
          onChange={onDescriptionChange}
        />
      </StyledDescriptionInputWrapper>
    </ConfirmationModal>
  );
};
